import { FormEvent, useState, useContext, useMemo } from "react";
import checkoutContext from "../../../../checkout-context";
import { FormDrawer } from "../../../../../drawer/FormDrawer";
import { CompanyDetailsForm } from "../../utils/forms/CompanyDetailsForm";
import { Address, CompanyDetails } from "../../types/Types";
import { InvoiceOptionSelector } from "../../utils/fields/InvoiceOptionSelector";
import { PersonalAddressForm } from "../../utils/forms/PersonalAddressForm";
import { isCompanyDetails } from "../../utils/IsCompanyDetails";
import { getFormDataObj } from "../../utils/getFormDataObj";

type ManageInvoiceOptionsDrawerProps = {
  options: (CompanyDetails | Address)[];
  onClose: (selectedOptionIndex?: number | null) => void;
  onSavePersonalAddress: (address: Address) => void;
  onSaveCompanyDetails: (
    companyDetails: CompanyDetails,
    index: number | null
  ) => void;
  defaultSelectedOptionIndex: number;
  defaultShowInvoiceOptionSelector?: boolean;
};

export function ManageInvoiceOptionsDrawer({
  options,
  onClose,
  onSavePersonalAddress,
  onSaveCompanyDetails,
  defaultSelectedOptionIndex,
  defaultShowInvoiceOptionSelector = true,
}: ManageInvoiceOptionsDrawerProps) {
  const { textsMapping } = useContext(checkoutContext);

  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(
    defaultSelectedOptionIndex
  );

  const selectedOption = useMemo(
    () =>
      typeof selectedOptionIndex === "number"
        ? options[selectedOptionIndex]
        : null,
    [options, selectedOptionIndex]
  );

  const [showInvoiceOptionSelector, setShowInvoiceOptionSelector] = useState(
    defaultShowInvoiceOptionSelector
  );

  const drawerTitle = useMemo(getDrawerTitle, [
    showInvoiceOptionSelector,
    selectedOption,
  ]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    const formElement = e.currentTarget;

    // Create
    if (selectedOption == undefined) {
      const formObj = getFormDataObj<CompanyDetails>(formElement);

      // Index of selected item is newly created item on the end of the options array.
      onSaveCompanyDetails(formObj, options.length);
      return;
    }

    if (isCompanyDetails(selectedOption)) {
      const formObj = getFormDataObj<CompanyDetails>(formElement);
      onSaveCompanyDetails(formObj, selectedOptionIndex);
    } else {
      const formObj = getFormDataObj<Address>(formElement);
      onSavePersonalAddress(formObj);
    }
  }

  function getDrawerTitle() {
    if (showInvoiceOptionSelector) {
      return textsMapping.drawerSelectInvoiceOptionTitle;
    }

    if (selectedOptionIndex == null) {
      return textsMapping.drawerAddCompanyInvoiceOptionTitle;
    }

    if (isCompanyDetails(selectedOption)) {
      return textsMapping.drawerEditCompanyInvoiceOptionTitle;
    } else {
      return textsMapping.drawerEditPersonalAddressTitle;
    }
  }

  return (
    <FormDrawer
      onSubmit={handleSubmit}
      onClose={() => onClose()}
      title={drawerTitle}
      primaryLabel={
        showInvoiceOptionSelector
          ? textsMapping.drawerConfirmButtonText
          : textsMapping.drawerSaveButtonText
      }
      secondaryLabel={textsMapping.drawerCancelButtonText}
      onPrimaryButtonClick={
        showInvoiceOptionSelector
          ? () => onClose(selectedOptionIndex)
          : undefined
      }
      onSecondaryButtonClick={
        showInvoiceOptionSelector
          ? undefined
          : () => setShowInvoiceOptionSelector(true)
      }
      disablePrimaryButton={
        showInvoiceOptionSelector && selectedOptionIndex === null
      }
    >
      {showInvoiceOptionSelector ? (
        <InvoiceOptionSelector
          options={options}
          selectedOptionIndex={selectedOptionIndex}
          onOptionSelect={setSelectedOptionIndex}
          onAdd={() => {
            setSelectedOptionIndex(null);
            setShowInvoiceOptionSelector(false);
          }}
          onEdit={() => setShowInvoiceOptionSelector(false)}
        />
      ) : selectedOption == null ? (
        <CompanyDetailsForm defaultValues={{}} />
      ) : isCompanyDetails(selectedOption) ? (
        <CompanyDetailsForm defaultValues={selectedOption} />
      ) : (
        <PersonalAddressForm defaultValues={selectedOption} />
      )}
    </FormDrawer>
  );
}
